import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Save, Building2, Upload, Image, X, Users, Shield, UserPlus, Trash2, Crown, Eye, Copy, Check, CreditCard, CheckCircle, Calculator, BellRing } from "lucide-react";

export default function SettingsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("company");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: "Malla & Co Pty Ltd",
    contact_name: "Harish Malla",
    abn: "35 679 211 691",
    default_address: "24 Quarter Circuit\nSpringvale VIC 3171 Australia",
    bank_name: "Commonwealth Bank (CBA)",
    bank_account_name: "MALLA & CO PTY LTD",
    bank_bsb: "063 171",
    bank_account_number: "114 117 56",
    gst_rate: 10,
    logo_base64: null,
    entity_type: "company",
    super_contributions_annual: 0,
    other_deductions_annual: 0,
    gst_registered: true,
    auto_reminders_enabled: false,
    auto_reminder_days: [7, 14, 30],
  });

  // Team management state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "member" });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const fileRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSettings(); }, []);
  useEffect(() => { if (activeTab === "team") fetchMembers(); }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`, { headers });
      setSettings(response.data);
    } catch (err) {
      console.error("Failed to load company settings, using defaults:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    setMembersLoading(true);
    try {
      const res = await axios.get(`${API}/org/members`, { headers });
      setMembers(res.data || []);
    } catch {
      toast.error("Failed to load team members");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/org/invite`, inviteForm, { headers });
      setInviteResult(res.data);
      fetchMembers();
      toast.success("Team member added successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite member");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/org/members/${userId}/role`, { role: newRole }, { headers });
      setMembers(m => m.map(member => member.user_id === userId ? { ...member, role: newRole } : member));
      toast.success("Role updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from your organization?`)) return;
    try {
      await axios.delete(`${API}/org/members/${userId}`, { headers });
      setMembers(m => m.filter(member => member.user_id !== userId));
      toast.success("Member removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company`, settings, { headers });
      toast.success("Settings saved successfully");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPEG, PNG, GIF, WebP, or SVG images are supported");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2MB");
      return;
    }
    setLogoUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/settings/logo`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setSettings(s => ({ ...s, logo_base64: res.data.logo_base64 }));
      toast.success("Logo uploaded — it will appear on all new PDFs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await axios.put(`${API}/settings/company`, { ...settings, logo_base64: "" }, { headers });
      setSettings(s => ({ ...s, logo_base64: null }));
      toast.success("Logo removed");
    } catch {
      toast.error("Failed to remove logo");
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading settings...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Settings">
      <div className="space-y-6 max-w-3xl" data-testid="settings-page">

        {/* Tab Navigation */}
        <div className="flex gap-1 border-b border-border">
          {[
            { id: "company", label: "Company", icon: Building2 },
            { id: "team", label: "Team Members", icon: Users },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`settings-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Company Settings Tab */}
        {activeTab === "company" && (
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Company Logo */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Image className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Company Logo</CardTitle>
                  <CardDescription>Appears top-left on all PDF invoices, above your company name</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={e => handleLogoUpload(e.target.files[0])} data-testid="logo-file-input" />

              {settings.logo_base64 ? (
                <div className="flex items-start gap-6">
                  {/* Preview */}
                  <div className="border rounded-lg p-3 bg-white flex items-center justify-center" style={{ minWidth: 160, minHeight: 80 }}>
                    <img src={settings.logo_base64} alt="Company logo" style={{ maxWidth: 150, maxHeight: 70, objectFit: "contain" }} />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span>Logo uploaded — will appear on invoices</span>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={logoUploading}
                        data-testid="change-logo-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        {logoUploading ? "Uploading..." : "Change Logo"}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={removeLogo} data-testid="remove-logo-btn">
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">PNG or JPEG recommended · Max 2MB · Max 40mm × 20mm on PDF</p>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                  data-testid="logo-upload-zone"
                  onDrop={e => { e.preventDefault(); handleLogoUpload(e.dataTransfer.files[0]); }}
                  onDragOver={e => e.preventDefault()}
                >
                  {logoUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-muted-foreground">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground/50" />
                      <p className="text-sm font-medium">Click or drag to upload your logo</p>
                      <p className="text-xs text-muted-foreground">PNG, JPEG, SVG · Max 2MB · Will appear top-left on invoices</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Company Details</CardTitle>
                  <CardDescription>Your business information for invoices</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input id="company_name" value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    placeholder="Malla & Co Pty Ltd" data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input id="contact_name" value={settings.contact_name}
                    onChange={(e) => setSettings({ ...settings, contact_name: e.target.value })}
                    placeholder="Harish Malla" data-testid="contact-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input id="abn" value={settings.abn}
                    onChange={(e) => setSettings({ ...settings, abn: e.target.value })}
                    placeholder="35 679 211 691" data-testid="abn-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_rate">GST Rate (%)</Label>
                  <Input id="gst_rate" type="number" step="0.1" value={settings.gst_rate}
                    onChange={(e) => setSettings({ ...settings, gst_rate: parseFloat(e.target.value) || 10 })}
                    data-testid="gst-rate-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_address">Default Business Address</Label>
                <Textarea id="default_address" value={settings.default_address}
                  onChange={(e) => setSettings({ ...settings, default_address: e.target.value })}
                  placeholder="24 Quarter Circuit&#10;Springvale VIC 3171 Australia"
                  rows={3} data-testid="default-address-input" />
                <p className="text-xs text-muted-foreground">
                  This address appears on invoices. Override per client in Client settings.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Finance Advisor Inputs */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Finance Advisor Inputs</CardTitle>
                  <CardDescription>Powers the P&amp;L, tax & take-home analysis on the Finance Advisor page</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="entity_type">Default Entity Type</Label>
                  <select id="entity_type" value={settings.entity_type || "company"}
                    onChange={(e) => setSettings({ ...settings, entity_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="entity-type-select">
                    <option value="company">Company (Pty Ltd — 25% base rate)</option>
                    <option value="sole_trader">Sole Trader (ATO individual brackets)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="super_contrib">Super Contributions (annual, $)</Label>
                  <Input id="super_contrib" type="number" min="0" step="100"
                    value={settings.super_contributions_annual ?? 0}
                    onChange={(e) => setSettings({ ...settings, super_contributions_annual: parseFloat(e.target.value) || 0 })}
                    placeholder="0" data-testid="super-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="other_deductions">Other Annual Deductions ($)</Label>
                  <Input id="other_deductions" type="number" min="0" step="100"
                    value={settings.other_deductions_annual ?? 0}
                    onChange={(e) => setSettings({ ...settings, other_deductions_annual: parseFloat(e.target.value) || 0 })}
                    placeholder="0" data-testid="other-deductions-input" />
                  <p className="text-xs text-muted-foreground">
                    Recurring deductions not captured in Expenses/Bills (e.g. home-office %).
                  </p>
                </div>
                <div className="space-y-2 flex flex-col justify-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none pb-2">
                    <input type="checkbox"
                      checked={!!settings.gst_registered}
                      onChange={(e) => setSettings({ ...settings, gst_registered: e.target.checked })}
                      className="h-4 w-4 accent-primary"
                      data-testid="gst-registered-checkbox" />
                    <span>GST-registered business</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auto Payment Reminders */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <BellRing className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Auto Payment Reminders</CardTitle>
                  <CardDescription>Automatically email clients when invoices become overdue</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer select-none hover:bg-muted/40">
                <input type="checkbox"
                  checked={!!settings.auto_reminders_enabled}
                  onChange={(e) => setSettings({ ...settings, auto_reminders_enabled: e.target.checked })}
                  className="h-4 w-4 accent-primary mt-0.5"
                  data-testid="auto-reminders-enabled-checkbox" />
                <div>
                  <div className="font-medium">Enable auto-reminders</div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    A background job runs every 6 hours. For each unpaid invoice, it sends a professional reminder email at
                    the milestones below — once per milestone per invoice. Same template as the manual reminder.
                  </p>
                </div>
              </label>
              <div className="space-y-2">
                <Label htmlFor="reminder_days">Reminder days after due date <span className="text-xs text-muted-foreground">(comma-separated)</span></Label>
                <Input id="reminder_days" type="text"
                  value={(settings.auto_reminder_days || []).join(", ")}
                  onChange={(e) => {
                    const parsed = e.target.value.split(",").map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n) && n > 0);
                    setSettings({ ...settings, auto_reminder_days: parsed });
                  }}
                  placeholder="7, 14, 30"
                  data-testid="reminder-days-input" />
                <p className="text-xs text-muted-foreground">
                  Defaults to 7, 14 and 30 days overdue. Requires client&apos;s contact_email to be set.
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Bank Details</CardTitle>
                  <CardDescription>Payment information shown on invoices</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input id="bank_name" value={settings.bank_name}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="Commonwealth Bank (CBA)" data-testid="bank-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_name">Account Name</Label>
                  <Input id="bank_account_name" value={settings.bank_account_name}
                    onChange={(e) => setSettings({ ...settings, bank_account_name: e.target.value })}
                    placeholder="MALLA & CO PTY LTD" data-testid="bank-account-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_bsb">BSB</Label>
                  <Input id="bank_bsb" value={settings.bank_bsb}
                    onChange={(e) => setSettings({ ...settings, bank_bsb: e.target.value })}
                    placeholder="063 171" data-testid="bank-bsb-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input id="bank_account_number" value={settings.bank_account_number}
                    onChange={(e) => setSettings({ ...settings, bank_account_number: e.target.value })}
                    placeholder="114 117 56" data-testid="bank-account-number-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="btn-hover" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
        )}

        {/* Team Members Tab */}
        {activeTab === "team" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Team Members</h3>
                <p className="text-sm text-muted-foreground">Manage who has access to your organization. Members have read-only access.</p>
              </div>
              <Button onClick={() => { setShowInviteDialog(true); setInviteResult(null); setInviteForm({ email: "", name: "", role: "member" }); }} data-testid="invite-member-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {membersLoading ? (
                  <div className="p-8 text-center text-muted-foreground">Loading members...</div>
                ) : members.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No team members yet. Add your first member above.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {members.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-4" data-testid={`member-row-${member.user_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                            {(member.name || member.email || "?")[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{member.name || member.email}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={member.role === "admin" ? "default" : "secondary"} className="flex items-center gap-1">
                            {member.role === "admin" ? <Crown className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                            {member.role === "admin" ? "Admin" : "Read Only"}
                          </Badge>
                          <Select value={member.role} onValueChange={(val) => handleRoleChange(member.user_id, val)}>
                            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid={`role-select-${member.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Read Only</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleRemoveMember(member.user_id, member.email)} data-testid={`remove-member-${member.user_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-semibold mb-1">Access Control</p>
                    <p><strong>Admin:</strong> Full access — create, edit, delete, manage team and settings.</p>
                    <p><strong>Read Only (Member):</strong> Can view all data but cannot create, edit, or delete anything.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invite Member Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
            </DialogHeader>

            {!inviteResult ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="invite-email">Email Address</Label>
                  <Input id="invite-email" type="email" placeholder="colleague@example.com" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} data-testid="invite-email-input" />
                </div>
                <div>
                  <Label htmlFor="invite-name">Full Name</Label>
                  <Input id="invite-name" placeholder="Jane Smith" value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} data-testid="invite-name-input" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={inviteForm.role} onValueChange={val => setInviteForm(f => ({ ...f, role: val }))}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Read Only (Member)</SelectItem>
                      <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                  <Button onClick={handleInvite} disabled={inviting || !inviteForm.email} data-testid="confirm-invite-btn">
                    {inviting ? "Adding..." : "Add Member"}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">Account Created</p>
                  <p className="text-sm text-green-700 dark:text-green-300">Share these credentials with <strong>{inviteResult.email}</strong>:</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-mono text-sm">{inviteResult.email}</p>
                    </div>
                  </div>
                  {inviteResult.temp_password && (
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Temporary Password</p>
                        <p className="font-mono text-sm font-bold">{inviteResult.temp_password}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(inviteResult.temp_password); setCopiedPassword(true); setTimeout(() => setCopiedPassword(false), 2000); }}>
                        {copiedPassword ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                  {!inviteResult.temp_password && (
                    <p className="text-sm text-muted-foreground">This user already had an account and has been added to your organization.</p>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={() => { setShowInviteDialog(false); setInviteResult(null); }} data-testid="close-invite-dialog">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}

